import axios from "axios";
import { useCallback, useState } from "react";
import { getAuthToken } from "../context/AuthContext";

const baseUrl = process.env.REACT_APP_BACKEND_URL + "api/items";

const api = axios.create({
  baseURL: baseUrl,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
// Add response interceptor for debugging
api.interceptors.response.use(
  (response) => {
    console.log("=== API RESPONSE SUCCESS ===");
    console.log("Status:", response.status);
    console.log("Data:", response.data);
    console.log("=== END API RESPONSE SUCCESS ===");
    return response;
  },
  (error) => {
    console.log("=== API RESPONSE ERROR ===");
    console.log("Error:", error);
    console.log("Status:", error.response?.status);
    console.log("Response data:", error.response?.data);
    console.log("Request URL:", error.config?.url);
    console.log("Request headers:", error.config?.headers);
    console.log("=== END API RESPONSE ERROR ===");
    return Promise.reject(error);
  },
);

const useItems = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const getCurrentCompanyItems = useCallback(async () => {
    try {
      const response = await api.get("/");
      return response.data;
    } catch (error) {
      console.error("Error fetching current company items:", error);
      throw error;
    }
  }, []);

  const updateCurrentCompanyItems = async (itemsData) => {
    console.log("updateCurrentCompanyItems called with data:", itemsData);
    return api.post(
      "/",
      { items: itemsData },
      {
        headers: { "Content-Type": "application/json" },
      },
    );
  };

  const deleteCurrentCompanyItems = useCallback(async () => {
    try {
      const response = await api.delete("/");
      return response.data;
    } catch (error) {
      console.error("Error deleting items:", error);
      throw error;
    }
  }, []);

  // Image upload (legacy base64 method)
  const uploadImage = async (imageData) => {
    console.log("uploadImage called with data length:", imageData?.length);

    return api.post(
      "/upload/upload-image",
      {
        imageData: imageData,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
  };

  // Media upload (multipart FormData with progress tracking - supports images + videos)
  const uploadMedia = (file, onProgress) => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append("file", file);

      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          const percent = Math.round((e.loaded / e.total) * 100);
          onProgress(percent);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve({ data: JSON.parse(xhr.responseText) });
          } catch {
            resolve({ data: { success: true } });
          }
        } else {
          try {
            const err = JSON.parse(xhr.responseText);
            reject(new Error(err.message || `Upload failed (${xhr.status})`));
          } catch {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        }
      };

      xhr.onerror = () => reject(new Error("Network error during upload"));
      xhr.ontimeout = () => reject(new Error("Upload timed out"));

      const token = getAuthToken();
      xhr.open("POST", `${baseUrl}/upload/upload-media`);
      if (token) {
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      }
      // Don't set Content-Type — browser sets multipart boundary automatically
      xhr.send(formData);
    });
  };

  const getImage = async (imageId) => {
    console.log("getImage called with ID:", imageId);
    // URL encode the imageId to handle S3 keys with slashes
    const encodedId = encodeURIComponent(imageId);
    return api.get(`/image/${encodedId}`);
  };

  const getImageUrl = async (imageId) => {
    console.log("getImageUrl called with ID:", imageId);
    // URL encode the imageId to handle S3 keys with slashes
    const encodedId = encodeURIComponent(imageId);
    return api.get(`/image-url/${encodedId}`);
  };

  const deleteImage = async (imageId) => {
    console.log("deleteImage called with ID:", imageId);
    // URL encode the imageId to handle S3 keys with slashes
    const encodedId = encodeURIComponent(imageId);
    return api.delete(`/image/${encodedId}`);
  };

  // Admin functions
  const getItemsByCompanyId = useCallback(async (companyId) => {
    try {
      const response = await api.get(`/${companyId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching items for company ${companyId}:`, error);
      throw error;
    }
  }, []);

  const updateItemsByCompanyId = useCallback(async (companyId, itemsData) => {
    try {
      const response = await api.post(`/${companyId}`, { items: itemsData });
      return response.data;
    } catch (error) {
      console.error(`Error updating items for company ${companyId}:`, error);
      throw error;
    }
  }, []);

  const deleteItemsByCompanyId = useCallback(async (companyId) => {
    try {
      const response = await api.delete(`/${companyId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting items for company ${companyId}:`, error);
      throw error;
    }
  }, []);

  const getAllCompanyItems = useCallback(async () => {
    try {
      const response = await api.get("/all");
      return response.data;
    } catch (error) {
      console.error("Error fetching all company items:", error);
      throw error;
    }
  }, []);

  return {
    getCurrentCompanyItems,
    updateCurrentCompanyItems,
    deleteCurrentCompanyItems,
    getItemsByCompanyId,
    updateItemsByCompanyId,
    deleteItemsByCompanyId,
    getAllCompanyItems,
    getImage,
    getImageUrl,
    uploadImage,
    uploadMedia,
    deleteImage,
    isLoading,
    error,
  };
};

export default useItems;
